import React, { useState } from "react";
import {
  X,
  Clock,
  FolderOpen,
  Video,
  Image,
  Link as LinkIcon,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import Card from "./ui/Card";
import Button from "./ui/Button";

const CreativeSubmissionInstructions = ({
  isModal = false,
  onClose = null,
}) => {
  const [isExpanded, setIsExpanded] = useState(isModal);

  const content = (
    <div className="space-y-6">
      {/* Submission Deadline */}
      <div className="bg-red-500/20 backdrop-blur-xl border border-red-400/30 rounded-xl p-4 shadow-lg">
        <div className="flex items-start">
          <Clock className="h-5 w-5 text-red-300 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-red-200 mb-2">
              📅 Submission Deadline
            </h3>
            <p className="text-red-100">
              All creatives must be submitted by{" "}
              <strong className="text-white">10:00 PM EST</strong> the night
              before they go live.
            </p>
          </div>
        </div>
      </div>

      {/* File Organization */}
      <div className="space-y-4">
        <div className="flex items-center">
          <FolderOpen className="h-5 w-5 text-[#B8460E] mr-2" />
          <h3 className="text-lg font-semibold text-white">
            📁 How to Organize Your Files
          </h3>
        </div>

        {/* Step 1 */}
        <div className="bg-blue-500/20 backdrop-blur-xl border border-blue-400/30 rounded-xl p-4 shadow-lg">
          <h4 className="text-md font-semibold text-blue-200 mb-2">
            Step 1: Create Your Main Folder
          </h4>
          <div className="space-y-2 text-blue-100">
            <div>
              <span className="font-medium">Name format:</span>{" "}
              <code className="bg-blue-400/20 backdrop-blur-sm px-2 py-1 rounded text-sm border border-blue-400/30">
                yourname
              </code>
            </div>
            <div>
              <span className="font-medium">Example:</span>{" "}
              <span className="font-mono text-sm bg-blue-400/20 backdrop-blur-sm px-2 py-1 rounded border border-blue-400/30">
                Jack
              </span>
            </div>
          </div>
        </div>

        {/* Step 2 */}
        <div className="bg-green-500/20 backdrop-blur-xl border border-green-400/30 rounded-xl p-4 shadow-lg">
          <h4 className="text-md font-semibold text-green-200 mb-2">
            Step 2: Create Date Subfolders
          </h4>
          <div className="space-y-2 text-green-100">
            <p>Inside your main folder, create subfolders for each date:</p>
            <div>
              <span className="font-medium">📁 month/day/year format</span>
            </div>
            <div>
              <span className="font-medium">Examples:</span>
              <div className="mt-1 space-x-2">
                <span className="font-mono text-sm bg-green-400/20 backdrop-blur-sm px-2 py-1 rounded border border-green-400/30">
                  01/15/2025
                </span>
                <span className="font-mono text-sm bg-green-400/20 backdrop-blur-sm px-2 py-1 rounded border border-green-400/30">
                  01/16/2025
                </span>
                <span className="text-gray-300">etc.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Step 3 */}
        <div className="bg-purple-500/20 backdrop-blur-xl border border-purple-400/30 rounded-xl p-4 shadow-lg">
          <h4 className="text-md font-semibold text-purple-200 mb-2">
            Step 3: Create Two Subfolders Inside Each Date
          </h4>
          <div className="space-y-2 text-purple-100">
            <div className="flex items-center">
              <span className="font-mono text-sm bg-purple-400/20 backdrop-blur-sm px-2 py-1 rounded mr-2 border border-purple-400/30">
                📁 videos
              </span>
              <span>(for all video files)</span>
            </div>
            <div className="flex items-center">
              <span className="font-mono text-sm bg-purple-400/20 backdrop-blur-sm px-2 py-1 rounded mr-2 border border-purple-400/30">
                📁 images
              </span>
              <span>(for all image files)</span>
            </div>
          </div>
        </div>

        {/* Step 4 */}
        <div className="bg-orange-500/20 backdrop-blur-xl border border-orange-400/30 rounded-xl p-4 shadow-lg">
          <h4 className="text-md font-semibold text-orange-200 mb-2">
            Step 4: Name Your Files
          </h4>
          <div className="space-y-3 text-orange-100">
            <div>
              <div className="font-medium mb-1">Videos:</div>
              <div className="space-x-2 text-sm">
                <code className="bg-orange-400/20 backdrop-blur-sm px-2 py-1 rounded border border-orange-400/30">
                  name.video.1
                </code>
                <code className="bg-orange-400/20 backdrop-blur-sm px-2 py-1 rounded border border-orange-400/30">
                  name.video.2
                </code>
                <code className="bg-orange-400/20 backdrop-blur-sm px-2 py-1 rounded border border-orange-400/30">
                  name.video.3
                </code>
              </div>
            </div>
            <div>
              <div className="font-medium mb-1">Images:</div>
              <div className="space-x-2 text-sm">
                <code className="bg-orange-400/20 backdrop-blur-sm px-2 py-1 rounded border border-orange-400/30">
                  name.image.1
                </code>
                <code className="bg-orange-400/20 backdrop-blur-sm px-2 py-1 rounded border border-orange-400/30">
                  name.image.2
                </code>
                <code className="bg-orange-400/20 backdrop-blur-sm px-2 py-1 rounded border border-orange-400/30">
                  name.image.3
                </code>
              </div>
            </div>
            <div className="mt-2">
              <div className="font-medium">Example:</div>
              <div className="space-x-2 text-sm">
                <code className="bg-orange-400/20 backdrop-blur-sm px-2 py-1 rounded border border-orange-400/30">
                  jack.video.1
                </code>
                <code className="bg-orange-400/20 backdrop-blur-sm px-2 py-1 rounded border border-orange-400/30">
                  jack.image.1
                </code>
              </div>
            </div>
          </div>
        </div>

        {/* Important Numbering */}
        <div className="bg-yellow-500/20 backdrop-blur-xl border border-yellow-400/30 rounded-xl p-4 shadow-lg">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-yellow-300 mr-3 mt-0.5 flex-shrink-0" />
            <div className="space-y-2 text-yellow-100">
              <h4 className="font-semibold text-yellow-200">
                ⚠️ IMPORTANT: Never Reset Your Numbers!
              </h4>
              <div className="space-y-1 text-sm">
                <div>
                  <strong>Day 1:</strong> jack.video.1, jack.video.2,
                  jack.video.3 (you used numbers 1-3)
                </div>
                <div>
                  <strong>Day 2:</strong> jack.video.4, jack.video.5,
                  jack.video.6 (continue from 4, not back to 1)
                </div>
                <div>
                  <strong>Day 3:</strong> jack.video.7, jack.video.8,
                  jack.video.9 (continue from 7)
                </div>
              </div>
              <div className="font-medium text-yellow-200 mt-2">
                Keep counting up forever - never start over!
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Requirements */}
      <div className="space-y-3">
        <div className="flex items-center">
          <Video className="h-5 w-5 text-purple-400 mr-2" />
          <h3 className="text-lg font-semibold text-white">
            🎥 Video Requirements
          </h3>
        </div>
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-300">
            <div className="space-y-2">
              <div>
                <strong className="text-white">Format:</strong> MP4, MOV
              </div>
              <div>
                <strong className="text-white">Duration:</strong> 15-60 seconds
              </div>
            </div>
            <div className="space-y-2">
              <div>
                <strong className="text-white">Aspect Ratios:</strong> 9:16
                (Stories/Reels), 4:3 (Feed), 1:1 (Square)
              </div>
              <div>
                <strong className="text-white">File size:</strong> No limit
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Requirements */}
      <div className="space-y-3">
        <div className="flex items-center">
          <Image className="h-5 w-5 text-green-400 mr-2" />
          <h3 className="text-lg font-semibold text-white">
            🖼️ Image Requirements
          </h3>
        </div>
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-300">
            <div className="space-y-2">
              <div>
                <strong className="text-white">Format:</strong> JPG, PNG
              </div>
              <div>
                <strong className="text-white">Minimum resolution:</strong>{" "}
                1080x1080
              </div>
            </div>
            <div className="space-y-2">
              <div>
                <strong className="text-white">Aspect Ratios:</strong> 9:16
                (Stories), 4:3 (Feed), 1:1 (Square)
              </div>
              <div>
                <strong className="text-white">File size:</strong> Under 30MB
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sharing Settings */}
      <div className="space-y-3">
        <div className="flex items-center">
          <LinkIcon className="h-5 w-5 text-blue-400 mr-2" />
          <h3 className="text-lg font-semibold text-white">
            🔗 Sharing Settings
          </h3>
        </div>
        <div className="bg-blue-500/20 backdrop-blur-xl border border-blue-400/30 rounded-xl p-4 shadow-lg">
          <p className="text-blue-100 mb-2">
            Make sure your Google Drive folder is set to:
          </p>
          <div className="bg-blue-400/20 backdrop-blur-sm rounded-lg p-3 text-center border border-blue-400/30">
            <span className="text-blue-200 font-semibold">
              "Anyone with the link can view"
            </span>
          </div>
        </div>
      </div>

      {/* Final Warning */}
      <div className="bg-yellow-500/20 backdrop-blur-xl border border-yellow-400/30 rounded-xl p-4 shadow-lg">
        <div className="flex items-start">
          <AlertTriangle className="h-5 w-5 text-yellow-300 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-yellow-200 mb-2">
              ⚠️ Important:
            </h4>
            <p className="text-yellow-100">
              Late submissions will not be accepted. The media buyer needs time
              to review and prepare campaigns for the next day.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <h2 className="text-2xl font-bold text-white">
              Creative Submission Instructions
            </h2>
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="p-6">{content}</div>
        </div>
      </div>
    );
  }

  return (
    <Card className="bg-white/5 backdrop-blur-2xl border border-white/10 shadow-xl">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 bg-white/10 backdrop-blur-xl hover:bg-white/20 transition-all duration-300 border-b border-white/10 relative overflow-hidden group"
      >
        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none"></div>

        <div className="flex items-center justify-between relative z-10">
          <div className="flex items-center">
            <FolderOpen className="h-5 w-5 text-[#B8460E] mr-3" />
            <span className="text-lg font-semibold text-white">
              Creative Submission Instructions
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-300">
              Guidelines & Requirements
            </span>
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-[#B8460E]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[#B8460E]" />
            )}
          </div>
        </div>
      </button>

      {isExpanded && <div className="p-6">{content}</div>}
    </Card>
  );
};

export default CreativeSubmissionInstructions;
